import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';
import { useNotifications } from '../NotificationsContext';

interface NotificationItem {
  id: number;
  user_id: number;
  type: string;
  message: string;
  created_at: number;
  read_at: number | null;
}

interface PagedResponse<T> {
  data: {
    items: T[];
    total: number;
  };
}

const NotificationsPage: React.FC = () => {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | 'error' | 'warning' | 'info'>('all');
  const { refreshUnread } = useNotifications();

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<PagedResponse<NotificationItem>>('/notifications', {
        params: { pageSize: 50 },
      });
      setItems(res.data.data.items);
    } catch (e) {
      setError('No se pudieron cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const markAsRead = async (id: number) => {
    try {
      await apiClient.patch(`/notifications/${id}`);
      setItems(prev =>
        prev.map(n => (n.id === id ? { ...n, read_at: n.read_at ?? Math.floor(Date.now() / 1000) } : n))
      );
      await refreshUnread();
    } catch (e) {
      // Silencioso, es una acción auxiliar
    }
  };

  const unreadCount = items.filter(n => n.read_at == null).length;
  const filteredByRead = showOnlyUnread ? items.filter(n => n.read_at == null) : items;
  const visibleItems =
    typeFilter === 'all'
      ? filteredByRead
      : filteredByRead.filter(n => n.type === typeFilter);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Notificaciones
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Revisa los avisos recientes del sistema y márcalos como leídos.
        </p>
      </header>

      <section
        style={{
          padding: 16,
          borderRadius: 18,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(148, 163, 184, 0.35)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de notificaciones
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 11,
                color: '#9ca3af',
              }}
            >
              Total: {items.length} · No leídas: {unreadCount}
              {showOnlyUnread && ' · Filtro: solo no leídas'}
              {typeFilter !== 'all' && ` · Tipo: ${typeFilter}`}
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => setShowOnlyUnread(prev => !prev)}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: showOnlyUnread
                  ? 'rgba(30, 64, 175, 0.5)'
                  : 'transparent',
                color: '#e5e7eb',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              {showOnlyUnread ? 'Ver todas' : 'Solo no leídas'}
            </button>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as typeof typeFilter)}
              style={{
                padding: '6px 8px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                color: '#e5e7eb',
                fontSize: 11,
                outline: 'none',
              }}
            >
              <option value="all">Todos los tipos</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>

        {error && (
          <div
            style={{
              color: '#fecaca',
              background: 'rgba(220, 38, 38, 0.12)',
              border: '1px solid rgba(248, 113, 113, 0.4)',
              borderRadius: 10,
              padding: '6px 8px',
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            {error}
          </div>
        )}

        {loading && <p style={{ fontSize: 12 }}>Cargando...</p>}
        {!loading && visibleItems.length === 0 && (
          <p style={{ fontSize: 12, color: '#9ca3af' }}>No hay notificaciones.</p>
        )}
        {!loading && visibleItems.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
              }}
            >
              <thead>
                <tr
                  style={{
                    textAlign: 'left',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                  }}
                >
                  <th style={{ padding: '6px 4px' }}>ID</th>
                  <th style={{ padding: '6px 4px' }}>Tipo</th>
                  <th style={{ padding: '6px 4px' }}>Mensaje</th>
                  <th style={{ padding: '6px 4px' }}>Fecha</th>
                  <th style={{ padding: '6px 4px' }}>Estado</th>
                  <th style={{ padding: '6px 4px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map(n => (
                  <tr
                    key={n.id}
                    style={{
                      borderBottom: '1px solid rgba(31, 41, 55, 0.9)',
                      backgroundColor: n.read_at ? 'transparent' : 'rgba(30, 64, 175, 0.18)',
                    }}
                  >
                    <td style={{ padding: '6px 4px' }}>{n.id}</td>
                    <td style={{ padding: '6px 4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          border: '1px solid rgba(148, 163, 184, 0.7)',
                          backgroundColor:
                            n.type === 'error'
                              ? 'rgba(220, 38, 38, 0.2)'
                              : n.type === 'warning'
                              ? 'rgba(234, 179, 8, 0.2)'
                              : 'rgba(37, 99, 235, 0.25)',
                          color:
                            n.type === 'error'
                              ? '#fecaca'
                              : n.type === 'warning'
                              ? '#facc15'
                              : '#bfdbfe',
                        }}
                      >
                        {n.type}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '6px 4px',
                        maxWidth: 260,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={n.message}
                    >
                      {n.message}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      {new Date(n.created_at * 1000).toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      {n.read_at ? 'Leída' : 'No leída'}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      {!n.read_at && (
                        <button
                          type="button"
                          onClick={() => markAsRead(n.id)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(56, 189, 248, 0.8)',
                            backgroundColor: 'rgba(15, 23, 42, 0.9)',
                            color: '#bae6fd',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Marcar como leída
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default NotificationsPage;
